import CallService from "@/lib/calls"
import { Trophy, Copy, Phone, CheckCircle, Clock, NavArrowDown } from "iconoir-react"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { Call } from "../../../lib/vibrato-api"
import { VibratoClientContext } from "../contexts"
import { RateCall } from "./RateCallModal"
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { LoadingSpinner } from "../../LoadingSpinner"

export default function CallResults({
  call,
  recordingUrl,
  children
}: {
  call: Call | null
  recordingUrl?: string | null
  children?: React.ReactNode
}): React.JSX.Element | null {
  if (!call) {
    return null
  }

  const client = useContext(VibratoClientContext)
  const navigate = useNavigate()
  const [retryError, setRetryError] = useState<string | null>(null)

  const callService = useMemo(() => new CallService(call), [call])

  const retryCall = useCallback(async () => {
    console.log("retrying call", call.id)
    setRetryError(null)

    try {
      const newCall = await client.createRetryCall(call.id)
      console.log("created new call", newCall)
      navigate(`/calls/${newCall.id}/edit`)
    } catch (err: any) {
      console.error("error creating new call", err)
      const errorMessage = err.data?.join(", ") || "Failed to create retry call. Please try again."
      setRetryError(errorMessage)
    }
  }, [call, client, navigate])

  const createTaskTemplate = useCallback(() => {
    console.log("creating task template", call.id)

    client
      .createTaskTemplateFromCall(call.id)
      .then((newTaskTemplate: any) => {
        console.log("created new task template", newTaskTemplate)
        navigate(`/task-templates/${newTaskTemplate.uuid}`)
      })
      .catch((err: any) => {
        console.error("error creating new task template", err)
      })
  }, [call, client, navigate])

  const successPill = (
    <span className="bg-green-100 text-green-700 font-medium text-xs px-2.5 py-1 rounded-full border border-green-200 flex items-center gap-1">
      <CheckCircle className="w-3 h-3" />
      Success
    </span>
  )

  const actionPill = (
    <span className="bg-amber-100 text-amber-700 font-medium text-xs px-2.5 py-1 rounded-full border border-amber-200 flex items-center gap-1">
      <Clock className="w-3 h-3" />
      Incomplete
    </span>
  )

  let currentPill: any = null
  if (call.succeeded) {
    currentPill = successPill
  } else if (call.succeeded === false) {
    currentPill = actionPill
  } else {
    currentPill = ""
  }

  const showMissingDataSummary = call.summary && callService.hasMissingData()

  // Helper function to check if call finished more than an hour ago
  const shouldCallHaveRecording = useCallback(() => {
    if (!call.completed_at) return false
    const completedAt = new Date(call.completed_at)
    const twentyMinutesAgo = new Date(Date.now() - 20 * 60 * 1000)
    return completedAt < twentyMinutesAgo
  }, [call])

  // Check if we're waiting for recording
  const isWaitingForRecording = () => {
    return !recordingUrl && call?.duration && call.duration > 0 && call.completed_at && !shouldCallHaveRecording()
  }

  // Check if we're waiting for results
  const isWaitingForResults = () => {
    return !call.summary
  }

  // Determine if we should show loading state
  const isLoading = () => {
    return isWaitingForRecording() || isWaitingForResults()
  }

  // Determine recording state
  const getRecordingContent = () => {
    if (recordingUrl) {
      // Show audio player if we have recording URL
      return (
        <div className="flex mb-6 justify-center w-full">
          <audio controls className="w-full max-w-md">
            <source src={recordingUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </div>
      )
    } else if (call.completed_at && shouldCallHaveRecording()) {
      // Show error message if call is older than an hour
      return (
        <div className="flex justify-center items-center mb-6">
          <div className="bg-gray-50 border border-gray-200 text-gray-700 p-4 rounded-lg text-center">
            <p className="text-sm">We couldn't process the recording for this call.</p>
          </div>
        </div>
      )
    }

    return null
  }

  return (
    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
      <Disclosure defaultOpen>
        {({ open }) => (
          <div>
            <DisclosureButton className="flex w-full justify-between items-center p-5 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Trophy className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-base font-medium text-gray-900">Call Results</h3>
              </div>
              <NavArrowDown
                className={`${
                  open ? "rotate-180 transform" : ""
                } h-5 w-5 text-blue-500 transition-transform duration-200`}
              />
            </DisclosureButton>

            <DisclosurePanel className="border-t border-gray-200 p-5">
              <div className="flex flex-wrap justify-center items-center gap-2 mb-4">
                <div className="tracking-wide font-medium text-lg">
                  {call.succeeded ? "Call Succeeded" : "Call Finished"}
                </div>
                {currentPill}
              </div>

              {getRecordingContent()}

              {isLoading() && (
                <div className="flex justify-center items-center gap-3 mb-6">
                  <LoadingSpinner className="w-5 h-5 text-purple-500" />
                  <p className="text-gray-800">
                    {isWaitingForRecording() && isWaitingForResults()
                      ? "Loading recording and results..."
                      : isWaitingForRecording()
                        ? call.call_transferred
                          ? "Waiting until transferred call is completed for recording..."
                          : "Loading recording..."
                        : "Call finished. Waiting for results..."}
                  </p>
                </div>
              )}

              {!isLoading() && showMissingDataSummary ? (
                <div className="mb-6">
                  <div className="mb-2 text-sm font-medium text-blue-600">Summary</div>
                  <p className="text-gray-800">{call.summary}</p>

                  <div className="mt-4 mb-2 text-sm font-medium text-blue-600">Next Steps</div>
                  <p className="text-gray-800">
                    We hit a snag – it seems we&apos;re missing some key details. Click &rsquo;Retry Call&rsquo;, and on
                    the next screen, you&apos;ll have the chance to fill in the gaps.
                  </p>
                  <p className="mt-4 text-gray-800">
                    <span className="text-purple-600 font-medium">Fun fact 🎉</span>: over 90% of calls{" "}
                    <span className="text-green-600 font-medium">succeed</span> after a retry.
                  </p>
                </div>
              ) : (
                <>
                  {!isLoading() && call.summary != null && (
                    <div className="mb-6">
                      <div className="mb-2 text-sm font-medium text-blue-600">Summary</div>
                      <p className="text-gray-800">{call.summary}</p>

                      {!callService.succeeded() && (
                        <>
                          <div className="mt-4 mb-2 text-sm font-medium text-blue-600">Next Steps</div>
                          <p className="text-gray-800">
                            Sometimes, external factors can influence the outcome. Let&apos;s give it another shot –
                            click &rsquo;Retry Call&rsquo; to make the call again 💪🏼.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              {call.actions && call.actions.length > 0 && (
                <div className="mb-6">
                  <div className="mb-2 text-sm font-medium text-blue-600">Actions Needed</div>
                  <ul className="list-disc list-inside text-gray-800">
                    {call.actions.map((action, index) => (
                      <li key={index} className="mb-2">
                        <span className="font-medium">{action.name}</span>
                        <div className="mt-1 ml-5">{action.description}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <button
                  onClick={retryCall}
                  className="bg-linear-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                >
                  <span>Retry Call</span>
                  <Phone className="h-5 w-5" />
                </button>

                {call.dynamic_task && (
                  <button
                    className="bg-linear-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium py-3 px-6 rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                    onClick={createTaskTemplate}
                  >
                    <span>Create Task Template</span>
                    <Copy className="h-5 w-5" />
                  </button>
                )}
              </div>

              {retryError && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-center">
                  {retryError}
                </div>
              )}

              <RateCall call={call} callService={callService} />
            </DisclosurePanel>
          </div>
        )}
      </Disclosure>
      {children}
    </div>
  )
}
